import { z } from 'zod'

import {
	ApplicationError,
	type Collaborator,
	type News,
	type Project,
	type ProjectDetails,
	ProjectV2
} from '../domain'

const restrictedStrings = ['codigo', 'null', 'undefined', 'SSL']

export abstract class ForManagingProjects {
	static CreateSmartContract = z
		.object({
			smartContract: z.object({
				name: z
					.string()
					.min(3, { message: 'The name must be at least 3 characters long' })
					.max(32, { message: 'The name must be at most 32 characters long' })
					.regex(/^[a-zA-Z]/, { message: 'The name must start with a letter' })
					.regex(/^(?!.*[- ]$)(?!^[- ])/, {
						message: 'The name cannot start or end with a space or hyphen'
					})
					.regex(/^[a-zA-Z0-9-]+$/, {
						message: 'The name can only contain letters, numbers, and hyphens'
					})
			}),
			project: z.object({
				name: z
					.string()
					.regex(/^[a-zA-Z0-9-]+$/, {
						message: 'The name can only contain letters, numbers, and hyphens'
					})
					.regex(/^(?!.*[- ]$)(?!^[- ])/, {
						message: 'The name cannot start or end with a space or hyphen'
					})
					.min(3, { message: 'The name must be at least 3 character long' })
					.max(30, {
						message: 'The name must be at most 30 characters long'
					}),
				projectId: z
					.string()
					.regex(/^[a-z]/, {
						message: 'The name id must start with a lowercase letter'
					})
					.regex(/[a-z0-9]$/, {
						message: 'The name id cannot end with a hyphen'
					})
					.regex(/^[a-z][a-z0-9-]*[a-z0-9]$/, {
						message:
							'The name id can only contain lowercase letters, numbers, and hyphens'
					})
					.min(6, {
						message: 'The name id must be at least 6 characters long'
					})
					.max(30, {
						message: 'The name id must be at most 30 characters long'
					})
					.refine(val => !restrictedStrings.includes(val), {
						message:
							"The name id cannot contain restricted strings such as 'codigo', 'null', 'undefined', and 'SSL'"
					})
					.optional()
			})
		})
		.required()

	static CreateProjectPayload = z
		.object({
			description: z.string(),
			name: z
				.string()
				.regex(/^[a-zA-Z0-9-]+$/, {
					message: 'The name can only contain letters, numbers, and hyphens'
				})
				.regex(/^(?!.*[- ]$)(?!^[- ])/, {
					message: 'The name cannot start or end with a space or hyphen'
				})
				.min(3, { message: 'The name must be at least 3 character long' })
				.max(30, {
					message: 'The name must be at most 30 characters long'
				}),
			projectId: z
				.string()
				.regex(/^[a-z]/, {
					message: 'The project id must start with a lowercase letter'
				})
				.regex(/[a-z0-9]$/, {
					message: 'The project id cannot end with a hyphen'
				})
				.regex(/^[a-z][a-z0-9-]*[a-z0-9]$/, {
					message:
						'The project id can only contain lowercase letters, numbers, and hyphens'
				})
				.min(6, {
					message: 'The project id must be at least 6 characters long'
				})
				.max(30, {
					message: 'The project id must be at most 30 characters long'
				})
				.refine(val => !restrictedStrings.includes(val), {
					message:
						"The project id cannot contain restricted strings such as 'codigo', 'null', 'undefined', and 'SSL'"
				})
		})
		.required()

	static AddCollaboratorPayload = z
		.object({
			publicProjectId: z.string(), // TODO: Same type than ForManagingDApps
			role: z.string(), // TODO: Add enum for roles, always must be 'maintainer' for now
			userHandler: z.string().email()
		})
		.required()

	static RemoveCollaboratorPayload = z
		.object({
			publicProjectId: z.string(), // TODO: Same type than ForManagingDApps
			collaboratorUsername: z.string()
		})
		.required()

	static AcceptCollaboratorInvitation = z
		.object({
			publicProjectId: z.string(), // TODO: Same type than ForManagingDApps
			userHandler: z.string().email()
		})
		.required()

	abstract getProjectsAndNews(): Promise<{
		data?: {
			news: News[]
			projects: ProjectV2[]
		}
		error?: ApplicationError
	}>

	abstract getUserProjects(): Promise<{
		data?: {
			news: News[]
			projects: Project[]
		}
		error?: ApplicationError
	}>

	abstract getUserProjectDetails(id: string): Promise<{
		data?: ProjectDetails
		error?: ApplicationError
	}>

	abstract createProject(
		payload: z.infer<typeof ForManagingProjects.CreateProjectPayload>
	): Promise<{
		data?: Project
		error?: ApplicationError
	}>

	abstract createSmartContract(
		payload: z.infer<typeof ForManagingProjects.CreateSmartContract>
	): Promise<{
		data?: ProjectV2
		error?: ApplicationError
	}>

	abstract addCollaborator(
		payload: z.infer<typeof ForManagingProjects.AddCollaboratorPayload>
	): Promise<{
		data?: Collaborator
		error?: ApplicationError
	}>

	abstract removeCollaborator(
		payload: z.infer<typeof ForManagingProjects.RemoveCollaboratorPayload>
	): Promise<{
		error?: ApplicationError
	}>

	abstract acceptCollaboratorInvitation(
		payload: z.infer<typeof ForManagingProjects.AcceptCollaboratorInvitation>
	): Promise<{
		data?: ProjectV2
		error?: ApplicationError
	}>
}
